
/*
 * IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA
 * GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT
 *
 * Copyright: 2023 by Idemia Identity & Security USA LLC. All rights reserved.
 * License: In accordance  Idemia I&S USA LLC's license agreement.
 * Code Classification: GOVERNMENT
 *
 * Classification Person: Nadim Bakizada nadim.bakizada@us.idemia.com
 * Classification Reason: Software not specific to any U.S. Government Entity
 * Classification Date: 2023
 *
 * GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT - GOVERNMENT
 * IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA IDEMIA
 */

import React, { useEffect } from "react";
import { urls } from '../urls'
import { useUserStore } from "state";


export const useApplicantData = (registrationId: string) => {
    const [applicantData, setApplicantData] = React.useState<any>();
    const [responseAssociationData, setResponseAssociationData] = React.useState<any>();
    const [responseApplicantSid, setResponseApplicantSid] = React.useState<string>();
    const [responseFbiENumber, setResponseFbiENumber] = React.useState<string>();

    const adjudicationUrl = urls.ADJUDICATION
    const personUrl = urls.PERSON;
    const config = useUserStore(state => state.config);

    useEffect(() => {
        const controller = new AbortController();
        const { signal } = controller;
        let fetchAdjudicationUrl = `${process.env.NODE_ENV === 'production' ? adjudicationUrl : ""}/adjudication/association/${registrationId}`;

        // Config-driven: which response type (type1.field4) to pull the SID from
        const sidSourceField4: string | undefined = config?.pages?.['applicant-details']?.responseData?.sidSourceField4;
        // Config-driven: which nist field holds the SID (from nistParsing config)
        const nistFields = config?.nistParsing?.fields;
        const sidFieldConfig = nistFields?.find((f: any) => f.name === 'sid');
        const sidKey = sidFieldConfig?.key ?? 'field95';
        const sidType = sidFieldConfig?.type ?? 2;

        // Config-driven: which nist field holds the FBI E number (from nistParsing config)
        const eNumberFieldConfig = nistFields?.find((f: any) => f.name === 'fbiENumber');
        const eNumberKey = eNumberFieldConfig?.key ?? 'field9';
        const eNumberType = eNumberFieldConfig?.type ?? 1;

        const fetchUrls = async () => {
            try {
                const { associations } = await (await fetch(fetchAdjudicationUrl, {
                    credentials: "include",
                    signal
                })).json();

                console.log('debugging tcn click results associations: ', { associations, filtered: associations.filter((association: any) => association['type'] === 'Response') })

                if (associations.length) {
                    const filteredAssociations = associations.filter((association: any) => association.type === 'Response');
                    setResponseAssociationData(filteredAssociations)

                    // Fetch the Original association for resubmission data
                    const originalAssociation = associations.find((association: any) => association.type === 'Original');
                    if (originalAssociation) {
                        let fetchingPersonUrl = `${process.env.NODE_ENV === 'production' ? personUrl : ""}/registration/${originalAssociation.id}`;
                        const originalData = await (await fetch(fetchingPersonUrl, {
                            credentials: "include",
                            signal
                        })).json();
                        if (originalData) {
                            setApplicantData({ ...originalData, tcn: originalAssociation.tcn });
                        }
                    }

                    // Fetch all response registrations and find the SID from the configured source
                    const responsePromises = filteredAssociations.map(async (repAssociation: any) => {
                        let fetchingPersonUrl = `${process.env.NODE_ENV === 'production' ? personUrl : ""}/registration/${repAssociation.id}`;
                        const data = await (await fetch(fetchingPersonUrl, {
                            credentials: "include",
                            signal
                        })).json();
                        return data;
                    });

                    const responses = await Promise.all(responsePromises);


                    // Find SID from the configured response type (type1.field4)
                    let sidFound = false;
                    for (const data of responses) {
                        if (!data) continue;
                        const field4 = data.descriptors?.data?.type1?.field4;
                        const typeData = sidType === 1 ? data.descriptors?.data?.type1 : data.descriptors?.data?.type2;
                        const sid = typeData?.[sidKey];

                        if (sidSourceField4) {
                            if (field4 === sidSourceField4 && sid) {
                                setResponseApplicantSid(sid);
                                sidFound = true;
                                break;
                            }
                        } else {
                            if (sid) {
                                setResponseApplicantSid(sid);
                                sidFound = true;
                                break;
                            }
                        }
                    }

                    if (!sidFound) {
                        console.warn(`[useApplicantData] No SID found${sidSourceField4 ? ` for configured source type1.field4="${sidSourceField4}"` : ''}`);
                    }

                    // Find FBI E number from the response, using configured source type and nist field
                    const fbiENumberSourceField4: string | undefined = config?.pages?.['applicant-details']?.responseData?.fbiEnumberSourceField4;
                    for (const data of responses) {
                        if (!data) continue;
                        const field4 = data.descriptors?.data?.type1?.field4;
                        const typeData = eNumberType === 1 ? data.descriptors?.data?.type1 : data.descriptors?.data?.type2;
                        const eNumber = typeData?.[eNumberKey];

                        if (fbiENumberSourceField4) {
                            if (field4 === fbiENumberSourceField4 && eNumber) {
                                setResponseFbiENumber(eNumber);
                                break;
                            }
                        } else {
                            if (eNumber) {
                                setResponseFbiENumber(eNumber);
                                break;
                            }
                        }
                    }
                }

            } catch (err: any) {
                console.log('ERROR => ', err);
            }
        }

        fetchUrls();

    }, [registrationId, adjudicationUrl, personUrl, config])

    return { applicantData, responseAssociationData, responseApplicantSid, responseFbiENumber };
};
